import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Calendar, MapPin, User, Cake, Heart, ArrowRight, Users, Image as ImageIcon, Bell, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;
const LIONS_LOGO = 'https://customer-assets.emergentagent.com/job_jaipur-lions/artifacts/ex49q6vl_district_logo_lionsclub-removebg.png';

const HomePage = () => {
  const { user } = useOutletContext();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [highlights, setHighlights] = useState({ birthdays_today: [], anniversaries_today: [] });
  const [latestMedia, setLatestMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ members: 0, events: 0, media: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, highlightsRes, mediaRes, membersRes] = await Promise.all([
        axios.get(`${API}/events?status=upcoming`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/dashboard/highlights`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/media?status=approved`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      
      setUpcomingEvents(eventsRes.data.slice(0, 5));
      setHighlights(highlightsRes.data);
      setLatestMedia(mediaRes.data.slice(0, 6));
      setStats({
        members: membersRes.data.length,
        events: eventsRes.data.length,
        media: mediaRes.data.length
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  // Feature cards for mobile grid
  const featureCards = [
    {
      path: '/app/members',
      icon: Users,
      title: 'Members',
      subtitle: `${stats.members} Active`,
      gradient: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
      image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&q=80'
    },
    {
      path: '/app/events',
      icon: Calendar,
      title: 'Events',
      subtitle: `${stats.events} Upcoming`,
      gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&q=80'
    },
    {
      path: '/app/gallery',
      icon: ImageIcon,
      title: 'Gallery',
      subtitle: `${stats.media} Photos`,
      gradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80'
    },
    {
      path: '/app/profile',
      icon: User,
      title: 'My Profile',
      subtitle: 'View & Edit',
      gradient: 'linear-gradient(135deg, #DC2626 0%, #F87171 100%)',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80'
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Mobile View */}
      <div className="md:hidden px-4 py-4 space-y-6">
        {/* Welcome Banner */}
        <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)' }}>
          <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
            <img src={LIONS_LOGO} alt="" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ color: '#FFFFFF' }}>
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-sm" style={{ color: '#94A3B8' }}>
            Stay connected with your Lions family
          </p>
          <div className="mt-4 flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}>
              {user?.role || 'Member'}
            </span>
          </div>
        </div>
        
        {/* Feature Cards Grid */}
        <div className="grid grid-cols-2 gap-4">
          {featureCards.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.path} to={card.path}>
                <div
                  className="rounded-2xl overflow-hidden relative h-40 hover:scale-105 transition-transform"
                  style={{ background: card.gradient }}
                >
                  <div className="absolute inset-0 bg-black/20"></div>
                  <img
                    src={card.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay"
                  />
                  <div className="relative p-4 h-full flex flex-col justify-between">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{card.title}</h3>
                      <p className="text-xs text-white/70">{card.subtitle}</p>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        
        {/* Today's Highlights */}
        {(highlights.birthdays_today.length > 0 || highlights.anniversaries_today.length > 0) && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>Today's Highlights</h2>
            
            {highlights.birthdays_today.length > 0 && (
              <div className="rounded-2xl p-4" style={{ backgroundColor: '#1E293B' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Cake className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <span className="font-medium" style={{ color: '#FFFFFF' }}>Birthdays Today</span>
                </div>
                <div className="space-y-3">
                  {highlights.birthdays_today.slice(0, 3).map((member) => (
                    <Link
                      key={member.user_id}
                      to={`/app/members/${member.user_id}`}
                      className="flex items-center gap-3 p-2 rounded-xl transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                    >
                      <img
                        src={getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                        alt={member.name}
                        className="w-10 h-10 rounded-full border-2"
                        style={{ borderColor: '#D4AF37' }}
                      />
                      <div>
                        <p className="font-medium" style={{ color: '#FFFFFF' }}>{member.name}</p>
                        <p className="text-xs" style={{ color: '#64748B' }}>{member.professional_category || 'Member'}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            {highlights.anniversaries_today.length > 0 && (
              <div className="rounded-2xl p-4" style={{ backgroundColor: '#1E293B' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <span className="font-medium" style={{ color: '#FFFFFF' }}>Anniversaries Today</span>
                </div>
                <div className="space-y-3">
                  {highlights.anniversaries_today.slice(0, 3).map((member) => (
                    <Link
                      key={member.user_id}
                      to={`/app/members/${member.user_id}`}
                      className="flex items-center gap-3 p-2 rounded-xl transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                    >
                      <img
                        src={getImageUrl(member.joint_photo_url) || getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                        alt={member.name}
                        className="w-10 h-10 rounded-full border-2"
                        style={{ borderColor: '#D4AF37' }}
                      />
                      <div>
                        <p className="font-medium" style={{ color: '#FFFFFF' }}>{member.name}</p>
                        {member.spouse_name && (
                          <p className="text-xs" style={{ color: '#D4AF37' }}>& {member.spouse_name}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>Upcoming Events</h2>
              <Link to="/app/events" className="text-sm" style={{ color: '#D4AF37' }}>
                View All
              </Link>
            </div>
            
            <div className="space-y-3">
              {upcomingEvents.slice(0, 3).map((event) => (
                <Link key={event.event_id} to={`/app/events/${event.event_id}`}>
                  <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1E293B' }}>
                    {event.banner_image_url && (
                      <div className="h-32 overflow-hidden">
                        <img
                          src={getImageUrl(event.banner_image_url)}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold mb-2" style={{ color: '#FFFFFF' }}>{event.title}</h3>
                      <div className="flex items-center gap-4 text-sm" style={{ color: '#64748B' }}>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(event.date_time_start)}</span>
                        </div>
                        {event.venue && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate">{event.venue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Latest Gallery */}
        {latestMedia.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>Latest Gallery</h2>
              <Link to="/app/gallery" className="text-sm" style={{ color: '#D4AF37' }}>
                View All
              </Link>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {latestMedia.slice(0, 6).map((media) => (
                <Link
                  key={media.media_id}
                  to="/app/gallery"
                  className="aspect-square rounded-xl overflow-hidden"
                >
                  {media.type === 'photo' ? (
                    <img
                      src={getImageUrl(media.url)}
                      alt={media.caption || 'Media'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)' }}>
                      <span className="text-white text-2xl">▶</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Desktop View - Keep existing design */}
      <div className="hidden md:block space-y-8 animate-fade-in">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>Welcome, {user?.name}</h1>
          <p className="text-muted-foreground">Stay connected with your Lions family</p>
        </div>
        
        {upcomingEvents.length > 0 ? (
          <section data-testid="upcoming-events-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold">Upcoming Events</h2>
              <Link to="/app/events">
                <Button variant="ghost" size="sm" data-testid="view-all-events-btn">
                  View All <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <Carousel className="w-full">
              <CarouselContent>
                {upcomingEvents.map((event) => (
                  <CarouselItem key={event.event_id} className="md:basis-1/2 lg:basis-1/3">
                    <Link to={`/app/events/${event.event_id}`}>
                      <Card className="hover-lift cursor-pointer" data-testid={`event-card-${event.event_id}`}>
                        {event.banner_image_url && (
                          <div className="h-48 overflow-hidden rounded-t-xl">
                            <img
                              src={getImageUrl(event.banner_image_url)}
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className="p-6">
                          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0F172A' }}>
                            {event.title}
                          </h3>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(event.date_time_start)}</span>
                            </div>
                            {event.venue && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{event.venue}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </section>
        ) : (
          <Card data-testid="no-events-card">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No upcoming events at the moment</p>
            </CardContent>
          </Card>
        )}
        
        {(highlights.birthdays_today.length > 0 || highlights.anniversaries_today.length > 0) && (
          <section data-testid="highlights-section">
            <h2 className="text-2xl font-semibold mb-4">Today's Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {highlights.birthdays_today.length > 0 && (
                <Card data-testid="birthdays-today-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Cake className="w-5 h-5" style={{ color: '#D4AF37' }} />
                      Birthdays Today
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {highlights.birthdays_today.map((member) => (
                        <Link
                          key={member.user_id}
                          to={`/app/members/${member.user_id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-all"
                        >
                          <img
                            src={getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                            alt={member.name}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-sm text-muted-foreground">{member.professional_category || 'Member'}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {highlights.anniversaries_today.length > 0 && (
                <Card data-testid="anniversaries-today-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5" style={{ color: '#D4AF37' }} />
                      Anniversaries Today
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {highlights.anniversaries_today.map((member) => (
                        <Link
                          key={member.user_id}
                          to={`/app/members/${member.user_id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-all"
                        >
                          <img
                            src={getImageUrl(member.joint_photo_url) || getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                            alt={member.name}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <p className="font-medium">{member.name}</p>
                            {member.spouse_name && (
                              <p className="text-sm text-muted-foreground">& {member.spouse_name}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </section>
        )}
        
        {latestMedia.length > 0 && (
          <section data-testid="latest-media-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold">Latest Photos & Videos</h2>
              <Link to="/app/gallery">
                <Button variant="ghost" size="sm" data-testid="view-gallery-btn">
                  View Gallery <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {latestMedia.map((media) => (
                <Link
                  key={media.media_id}
                  to="/app/gallery"
                  className="aspect-square rounded-xl overflow-hidden hover-lift"
                  data-testid={`media-item-${media.media_id}`}
                >
                  {media.type === 'photo' ? (
                    <img
                      src={getImageUrl(media.url)}
                      alt={media.caption || 'Media'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-royal flex items-center justify-center">
                      <span className="text-white text-4xl">▶</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default HomePage;
